import React, { useState, useEffect, useMemo } from "react";
import { Form, Button, Table, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";

import HRPagination from "components/HRPagination";
import ActionForm from "components/attendance/ActionForm";
import ManageStatus from "./ManageStatus";

import { useGetRequestLeavesQuery } from "state/slices/requestleave/requestLeaveApiSlice";
import { setRequestLeaves } from "state/slices/requestleave/requestLeaveSlice";

import { LEAVE_STATUS, LEAVE_TYPES } from "constants";
import { formatDate as formatDateUtil } from "utils/date";

const RequestLeaveViewManagement = ({
  isAllowedToUpdate = false,
  isAllowedToDelete = false,
  selectedMonthYear,
  setSelectedMonthYear,
  handleCheckboxChange = () => {},
  handleEdit = () => {},
  deleteRequestLeaveManage = () => {},
  selectedRow = null,
  statusData = {},
  onStatusUpdate = () => {},
}) => {
  const dispatch = useDispatch();

  const { userInfo: loggedUserInfo } = useSelector((state) => state.auth);

  const hasHigherRole = loggedUserInfo?.role?.isAdmin || loggedUserInfo?.role?.isTeamWise;

  const queryArgs = hasHigherRole
    ? selectedMonthYear
    : { ...selectedMonthYear, id: loggedUserInfo?.employeeId };

  const { data, refetch, isLoading } = useGetRequestLeavesQuery(queryArgs);

  const requestLeaves = useSelector((state) => state.requestLeave.leaves);

  useEffect(() => {
    refetch();
  }, [selectedMonthYear]);

  useEffect(() => {
    if (data && data.requests) {
      dispatch(setRequestLeaves(data.requests));
    }
  }, [data?.requests?.length]);

  const ITEMS_PER_PAGE = 10;
  const [currentPage, setCurrentPage] = useState(1);

  const paginatedRequests = useMemo(() => {
    const indexOfLastItem = currentPage * ITEMS_PER_PAGE;
    const indexOfFirstItem = indexOfLastItem - ITEMS_PER_PAGE;
    return requestLeaves.slice(indexOfFirstItem, indexOfLastItem);
  }, [requestLeaves, currentPage]);

  const totalPages = Math.ceil(requestLeaves.length / ITEMS_PER_PAGE);

  const handleActionsSubmit = async (values) => {
    setSelectedMonthYear(values);
    setCurrentPage(1); // reset to first page
  };

  const renderReason = (row) => {
    if (row.reason.length > 30) {
      return (
        <OverlayTrigger
          trigger={['hover', 'focus']}
          placement="top"
          overlay={<Tooltip id={`tooltip-${row.id}`}>{row.reason}</Tooltip>}
        >
          <span>{row.reason.slice(0, 30)}...</span>
        </OverlayTrigger>
      );
    }
    return row.reason;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    // Dates are now in ISO format (YYYY-MM-DD) from server
    // Format for display (e.g., DD-MM-YYYY)
    return formatDateUtil(dateString, 'dd-MM-yyyy') || dateString;
  };

  return (
    <>
      <div className="row justify-content-end">
        {isAllowedToUpdate && statusData.ready && (
          <div className="col-md-5">
            <ManageStatus
              statusData={statusData}
              onStatusUpdate={onStatusUpdate}
            />
          </div>
        )}
        <div className="col-md-7">
          <ActionForm
            onSubmit={handleActionsSubmit}
            attendanceYear={selectedMonthYear.year}
            attendanceMonth={selectedMonthYear.month}
          />
        </div>
      </div>

      <Table striped responsive bordered className="text-center mt-3">
        <thead>
          <tr>
            {(isAllowedToUpdate || isAllowedToDelete) && <th></th>}
            <th>S No.</th>
            {(isAllowedToUpdate || isAllowedToDelete) && <th>Employee Id</th>}
            <th>Leave Type</th>
            <th>Leave Date</th>
            <th>End Date</th>
            <th>Reason</th>
            <th>Status</th>
            <th>Changed By</th>
            {(isAllowedToUpdate || isAllowedToDelete) && <th>Action</th>}
          </tr>
        </thead>
        <tbody>
          {paginatedRequests.length > 0 ? (
            paginatedRequests.map((row, index) => (
              <tr key={row.id}>
                {(isAllowedToUpdate || isAllowedToDelete) && (
                  <td>
                    <Form.Check
                      type="checkbox"
                      name="row"
                      value={row.id}
                      checked={selectedRow?.id === row.id}
                      onChange={(e) => handleCheckboxChange(e, row)}
                    />
                  </td>
                )}
                <td>{(currentPage - 1) * ITEMS_PER_PAGE + index + 1}</td>
                {(isAllowedToUpdate || isAllowedToDelete) && (
                  <td>{row.employeeId}</td>
                )}
                <td>{LEAVE_TYPES[row.leaveType]}</td>
                <td>{formatDate(row.leaveDate)}</td>
                <td>{formatDate(row.endDate)}</td>
                <td>{renderReason(row)}</td>
                <td>{LEAVE_STATUS[row.status]}</td>
                <td>{row.approvedBy || row.rejectedBy || "—"}</td>
                {(isAllowedToUpdate || isAllowedToDelete) && (
                  <td>
                    {isAllowedToUpdate && (
                      <Button
                        size="sm"
                        variant="outline-primary"
                        className="me-2"
                        disabled={!selectedRow || selectedRow?.id !== row.id}
                        onClick={handleEdit}
                      >
                        <i className="bi bi-pencil-square"></i>
                      </Button>
                    )}
                    {isAllowedToDelete && (
                      <Button
                        size="sm"
                        variant="outline-danger"
                        disabled={!selectedRow || selectedRow?.id !== row.id}
                        onClick={deleteRequestLeaveManage}
                      >
                        <i className="bi bi-trash3"></i>
                      </Button>
                    )}
                  </td>
                )}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={10} className="text-muted">
                {isLoading ? "Loading Data..." : "No Data Found"}
              </td>
            </tr>
          )}
        </tbody>
      </Table>

      <div className="d-flex justify-content-end">
        <HRPagination
          totalPages={totalPages}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
        />
      </div>
    </>
  );
};

export default RequestLeaveViewManagement;
